// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * The live tuner: drives a ~20 Hz loop that reads mic frames, runs pitch
 * detection, names the note, finds the nearest string, and emits a smoothed
 * PitchReading. The loop uses setInterval (a hidden tab throttles rAF to a
 * halt — same trap noisewell/shakewell hit). A `hold` keeps the last note on
 * screen briefly through the silence between plucks so the display doesn't flicker.
 */

import { MicTap } from './audio';
import { detectPitch } from './pitch';
import { describeFrequency } from './notes';
import { getInstrument, nearestStringIndex } from './instruments';
import type { PitchReading, Settings } from './types';

const TICK_MS = 50; // 20 Hz
const IN_TUNE_CENTS = 5; // ± window that counts as "in tune"
const HOLD_MS = 700; // keep the last note visible through short silences

export interface TunerCallbacks {
  onReading: (r: PitchReading) => void;
  onError?: (msg: string) => void;
}

export class Tuner {
  private mic = new MicTap();
  private timer: ReturnType<typeof setInterval> | null = null;
  private settings: Settings;
  private readonly cb: TunerCallbacks;
  private smoothedHz = 0;
  private lastVoicedAt = 0;
  private lastReading: PitchReading | null = null;
  /** In test mode we feed frames manually instead of reading the mic. */
  private injectedFrame: Float32Array | null = null;
  private injectedRate = 48000;

  constructor(cb: TunerCallbacks, settings: Settings) {
    this.cb = cb;
    this.settings = { ...settings };
  }

  get isRunning(): boolean {
    return this.timer !== null;
  }

  updateSettings(s: Settings): void {
    this.settings = { ...s };
  }

  async startMic(): Promise<void> {
    await this.mic.start();
    this.startLoop();
  }

  /** Start the loop without a mic (test hook path). */
  startManual(): void {
    this.startLoop();
  }

  private startLoop(): void {
    if (this.timer) return;
    this.smoothedHz = 0;
    this.lastVoicedAt = 0;
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private tick(): void {
    const frame = this.injectedFrame ?? this.mic.readFrame();
    const rate = this.injectedFrame ? this.injectedRate : this.mic.sampleRate;
    if (!frame) return;

    const { frequency, clarity } = detectPitch(frame, rate, {
      minHz: 38,
      maxHz: 1600,
      clarityThreshold: 0.6,
    });

    const now = nowMs();
    if (frequency > 0) {
      // One-pole smoothing of the detected frequency for a steady needle.
      this.smoothedHz = this.smoothedHz > 0 ? this.smoothedHz + 0.35 * (frequency - this.smoothedHz) : frequency;
      this.lastVoicedAt = now;
      this.emit(this.smoothedHz, clarity);
    } else if (this.lastReading && now - this.lastVoicedAt < HOLD_MS) {
      // Hold the previous reading briefly (dimmed by the UI) rather than blank.
      this.cb.onReading({ ...this.lastReading, clarity: 0 });
    } else {
      this.smoothedHz = 0;
      this.lastReading = null;
      this.cb.onReading({ hz: null, clarity: 0, note: null, stringIndex: -1, inTune: false });
    }
  }

  private emit(hz: number, clarity: number): void {
    const { referenceA, accidental, instrument } = this.settings;
    const note = describeFrequency(hz, referenceA, accidental);
    const inst = getInstrument(instrument);
    const stringIndex = nearestStringIndex(hz, inst, referenceA);
    const inTune = note !== null && Math.abs(note.cents) <= IN_TUNE_CENTS;
    const reading: PitchReading = { hz, clarity, note, stringIndex, inTune };
    this.lastReading = reading;
    this.cb.onReading(reading);
  }

  // ── Test hooks ─────────────────────────────────────────────────────────
  injectFrame(frame: Float32Array, sampleRate: number): void {
    this.injectedFrame = frame;
    this.injectedRate = sampleRate;
    if (!this.timer) this.startManual();
    this.tick();
  }

  clearInjected(): void {
    this.injectedFrame = null;
  }

  micFullyStopped(): boolean {
    return this.mic.fullyStopped();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.injectedFrame = null;
    this.mic.stop();
    this.smoothedHz = 0;
    this.lastReading = null;
  }
}

function nowMs(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}
